import {
  DEFAULT_CITY_SLUG,
  DEFAULT_NICHE_SLUG,
  deriveLaunchCitySlug,
  deriveLaunchNicheSlug,
  findLaunchNicheBySlug,
  toSlug,
} from "../data/slugIndex";

export function safeDiscoveryValue(value) {
  return String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim();
}

export function firstDiscoveryValue(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const first = selectionValue(value[0]);
      if (safeDiscoveryValue(first)) return first;
      continue;
    }

    const candidate = selectionValue(value);
    if (safeDiscoveryValue(candidate)) return candidate;
  }

  return "";
}

export function normalizeDiscoveryTags(values) {
  if (!Array.isArray(values)) return [];

  return Array.from(
    new Set(
      values
        .map((value) =>
          safeDiscoveryValue(value)
            .toLowerCase()
            .replace(/^#+/, "")
            .replace(/\s+/g, "_")
        )
        .filter(Boolean)
    )
  );
}


function selectionValue(value) {
  if (!value || typeof value !== "object") return value;

  return (
    value.slug ||
    value.niche_slug ||
    value.nicheSlug ||
    value.city_slug ||
    value.citySlug ||
    value.id ||
    value.value ||
    value.name ||
    value.label ||
    ""
  );
}

export function resolveDiscoveryCitySlug(value, fallback = DEFAULT_CITY_SLUG) {
  const raw = safeDiscoveryValue(selectionValue(value));
  if (!raw) return fallback;

  return deriveLaunchCitySlug(raw, "") || toSlug(raw) || fallback;
}

export function resolveDiscoveryNicheSlug(value, fallback = DEFAULT_NICHE_SLUG) {
  const raw = safeDiscoveryValue(selectionValue(value));
  if (!raw) return fallback;

  return deriveLaunchNicheSlug(raw, "") || toSlug(raw) || fallback;
}

export function normalizeNicheMode(value, fallback = "") {
  const mode = safeDiscoveryValue(value).toLowerCase().replace("-", "_");
  if (mode === "parent" || mode === "child") return mode;

  const cleanFallback = safeDiscoveryValue(fallback)
    .toLowerCase()
    .replace("-", "_");

  return cleanFallback === "parent" || cleanFallback === "child"
    ? cleanFallback
    : "";
}

/**
 * Resolves the discovery mode from the launch niche index instead of a
 * hand-maintained slug map. Any new niche added to slugIndex therefore works
 * without another UI code change.
 */
export function getLaunchNicheMode(slug, fallbackMode = "") {
  const cleanSlug = resolveDiscoveryNicheSlug(slug, "");
  const niche = cleanSlug ? findLaunchNicheBySlug(cleanSlug) : null;

  const configuredMode = normalizeNicheMode(niche?.mode);
  if (configuredMode) return configuredMode;

  if (niche) {
    return niche.parent && niche.parent === niche.slug ? "parent" : "child";
  }

  return normalizeNicheMode(fallbackMode) || "child";
}

export function deriveDiscoverySegmentFromPreferences(
  preferences = {},
  selectedTags = []
) {
  const feedDefaults =
    preferences?.feedDefaults || preferences?.feed_defaults || {};

  const rawCity = firstDiscoveryValue(
    feedDefaults.city,
    feedDefaults.citySlug,
    feedDefaults.city_slug,
    preferences?.activeCitySlug,
    preferences?.active_city_slug,
    preferences?.selectedCity,
    preferences?.selected_city,
    preferences?.primaryCity,
    preferences?.primary_city,
    preferences?.selectedCitySlug,
    preferences?.selected_city_slug,
    preferences?.primaryCitySlug,
    preferences?.primary_city_slug,
    preferences?.citySlug,
    preferences?.city_slug,
    preferences?.locationSlug,
    preferences?.location_slug,
    preferences?.location,
    preferences?.city,
    preferences?.cities?.[0],
    preferences?.citySelections?.[0]?.slug,
    preferences?.city_selections?.[0]?.slug
  );

  const rawNiche = firstDiscoveryValue(
    feedDefaults.niche,
    feedDefaults.nicheSlug,
    feedDefaults.niche_slug,
    preferences?.activeNicheSlug,
    preferences?.active_niche_slug,
    preferences?.selectedNiche,
    preferences?.selected_niche,
    preferences?.primaryNiche,
    preferences?.primary_niche,
    preferences?.selectedNicheSlug,
    preferences?.selected_niche_slug,
    preferences?.primaryNicheSlug,
    preferences?.primary_niche_slug,
    preferences?.preferredNicheSlug,
    preferences?.preferred_niche_slug,
    preferences?.nicheSlug,
    preferences?.niche_slug,
    preferences?.niche,
    preferences?.niches?.[0],
    preferences?.nicheSelections?.[0]?.slug,
    preferences?.niche_selections?.[0]?.slug,
    preferences?.interests,
    preferences?.industry
  );

  const city = resolveDiscoveryCitySlug(rawCity, DEFAULT_CITY_SLUG);
  const niche = resolveDiscoveryNicheSlug(rawNiche, DEFAULT_NICHE_SLUG);

  const modeBySlug =
    preferences?.nicheModeBySlug || preferences?.niche_mode_by_slug || {};

  const requestedMode = firstDiscoveryValue(
    feedDefaults.nicheMode,
    feedDefaults.niche_mode,
    modeBySlug?.[niche],
    preferences?.selectedNiche?.mode,
    preferences?.selected_niche?.mode,
    preferences?.primaryNiche?.mode,
    preferences?.primary_niche?.mode,
    preferences?.nicheMode,
    preferences?.niche_mode,
    preferences?.nicheModes?.[0],
    preferences?.niche_modes?.[0]
  );

  return {
    platform: "instagram",
    city,
    niche,
    nicheMode: getLaunchNicheMode(niche, requestedMode),
    tags: normalizeDiscoveryTags(selectedTags),
  };
}
